import {
	Box,
	Card,
	Center,
	Collapse,
	Divider,
	Flex,
	Group,
	Loader,
	Stack,
	createStyles,
} from '@mantine/core';
import type { DataQualityScore, Filter } from '@repo/api-codegen';
import {
	useApiListEntityResultsByPolicy,
	useApiListPolicies,
	useRetrieveAggregateQuality,
} from '@repo/api-codegen';
import { trackEvent } from '@repo/common/analytics';
import { HoverCard } from '@repo/common/components/HoverCard';
import { Badge, Banner, Icon, Text, Title } from '@repo/foundations';
import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import type { ISecodaEntity } from '../../../api';
import { useAuthUser } from '../../../api';
import { useSuspenseFeatureAccess } from '../../../api/hooks/workspace/useSuspenseFeatureAccess';
import { EmptyState } from '../../../components/EmptyState';
import { appShellStore } from '../../../components/SecodaAppShell/store';
import { UpgradeButton } from '../../../components/Settings/UpgradeButton';
import { DataQualityBenchmarkTooltip } from '../DataQualityBenchmarkTooltip/DataQualityBenchmarkTooltip';
import { PolicyBreakdownList } from '../PolicyBreakdown';
import { QualityScoreRing } from '../QualityScoreRing/QualityScoreRing';

import { useMount } from 'ahooks';
import { QualityScorePercentage } from '../QualityScoreRing/QualityScorePercentage';
import { QualityScoreRingLoading } from '../QualityScoreRing/QualityScoreRingLoading';

const useStyles = createStyles((theme) => ({
	tooltipTarget: {
		borderBottom: `dotted 1px ${theme.other.getColor('border/secondary/default')}`,
	},
	ringCard: {
		overflow: 'visible',
	},
}));

interface DataQualityTabProps {
	entity: ISecodaEntity;
}

export function DataQualityTabV2({ entity }: DataQualityTabProps) {
	const { classes } = useStyles();
	const { user, workspace } = useAuthUser();
	const navigate = useNavigate();
	const { dqsAccess } = useSuspenseFeatureAccess();

	const [collapsedPolicies, setCollapsedPolicies] = useState<
		Record<string, boolean>
	>({});

	// Track page view
	useMount(() => {
		trackEvent('dqs/report/open', {}, user, workspace);
	});

	const { data: policies } = useApiListPolicies({});

	const { data: entityViolations, isLoading: isLoadingViolations } =
		useApiListEntityResultsByPolicy({
			pathParams: {
				entityId: entity.id,
			},
		});

	const hasViolationsData = !isLoadingViolations && entityViolations;
	const hasNoViolations =
		hasViolationsData && entityViolations.policies.length === 0;
	const hasViolations =
		hasViolationsData && entityViolations.policies.length > 0;

	// Calculate overall quality score from policy breakdown data
	const overallQualityScore = useMemo(() => {
		if (!hasViolationsData || entityViolations.policies.length === 0) {
			return 0;
		}

		let totalScore = 0;
		let totalPossible = 0;

		entityViolations.policies.forEach((policyBreakdown) => {
			const policyPossible = policyBreakdown.violations.reduce(
				(sum, v) => sum + v.points_possible,
				0
			);
			totalScore += policyBreakdown.total_score || 0;
			totalPossible += policyPossible;
		});

		return totalPossible > 0 ? (totalScore / totalPossible) * 100 : 0;
	}, [hasViolationsData, entityViolations]);

	const allPoliciesDisabled = useMemo(() => {
		if (!policies?.results || policies.results.length === 0) {
			return false;
		}
		return policies.results.every((policy) => !policy.enabled);
	}, [policies?.results]);

	const navigateToPolicies = () => {
		navigate('/policies');
	};

	const togglePolicyCollapse = (policyId: string) => {
		setCollapsedPolicies((prev) => ({
			...prev,
			[policyId]: !prev[policyId],
		}));
	};

	const setQualityForDownload = (quality: DataQualityScore) => {
		appShellStore.navBarUI.analyticsPage.downloadDataQualityScore.dataQualityScore =
			quality;
		return quality;
	};

	const handleClick = useCallback(
		(id: string) => {
			navigate(`/policy/${id}`);
			trackEvent('entity/policy/open', { policy_id: id }, user, workspace);
		},
		[navigate, user, workspace]
	);

	const { isLoading } = useRetrieveAggregateQuality(
		{
			queryParams: {
				filters: JSON.stringify({
					operator: 'and',
					operands: [],
				} as Filter),
			},
		},
		{
			select: setQualityForDownload,
		}
	);

	if (!dqsAccess) {
		return (
			<EmptyState
				title="Upgrade to access Quality Score"
				description="You can use the Data Quality Score to understand and improve your data quality."
				illustrationName="upgrade"
				includeGoBack={false}
				stateHeight="50vh"
				size="lg"
				withActions={<UpgradeButton feature="DQS" size="md" />}
			/>
		);
	}

	if (!workspace.quality_enabled) {
		return (
			<EmptyState
				illustrationName="upgrade"
				title="Quality Score is disabled"
				description="Quality Score is not enabled for this workspace. Please contact your administrator to turn on access."
				includeGoBack={false}
				stateHeight="80vh"
				size="lg"
			/>
		);
	}

	return (
		<Stack spacing="md" w="100%" pb="md">
			{allPoliciesDisabled && (
				<Banner
					tone="info"
					title="No data quality scores available"
					message="To see analytics, activate data quality policies"
					actions={[
						{
							children: 'Activate data quality policies',
							onClick: navigateToPolicies,
						},
					]}
				/>
			)}
			<Group position="apart" noWrap>
				<Card withBorder w={228} className={classes.ringCard}>
					<Group>
						{(isLoading || isLoadingViolations) && (
							<QualityScoreRingLoading size="xs" />
						)}
						{!isLoading && !isLoadingViolations && hasViolationsData && (
							<QualityScoreRing
								score={allPoliciesDisabled ? 0 : overallQualityScore}
								size="xs"
								borderWidth="sm"
								showInnerContent={false}
							/>
						)}
						<HoverCard
							target={
								<Stack spacing={0}>
									<Text
										color="text/secondary/default"
										align="center"
										size="sm"
										pb="xs"
									>
										Overall quality
									</Text>
									{hasViolationsData && (
										<QualityScorePercentage
											size="2xs"
											score={
												allPoliciesDisabled ? undefined : overallQualityScore
											}
											displayBadge={!allPoliciesDisabled}
											showUnavailableBadge={allPoliciesDisabled}
										/>
									)}
								</Stack>
							}
						>
							<DataQualityBenchmarkTooltip />
						</HoverCard>
					</Group>
				</Card>
				<Card withBorder>
					<Group>
						{isLoadingViolations && (
							<Center w="100%" p="md">
								<Loader size="sm" />
							</Center>
						)}
						{hasViolationsData && entityViolations.policies.length === 0 && (
							<Center w="100%" p="md">
								<Text color="text/secondary/default" size="sm">
									No policies configured
								</Text>
							</Center>
						)}
						{hasViolations &&
							entityViolations.policies.map((policyBreakdown, index) => {
								const totalPossiblePoints = policyBreakdown.violations.reduce(
									(sum, v) => sum + v.points_possible,
									0
								);
								const scorePercentage =
									totalPossiblePoints > 0
										? ((policyBreakdown.total_score || 0) /
												totalPossiblePoints) *
											100
										: 0;
								const isPolicyDisabled = !policyBreakdown.policy.enabled;

								return (
									<>
										<Box key={policyBreakdown.policy.id} w={108} pl="xs">
											<Text color="text/secondary/default" size="sm" pb="xs">
												{policyBreakdown.policy.name}
											</Text>
											<Group spacing="xs">
												<QualityScoreRing
													score={isPolicyDisabled ? 0 : scorePercentage}
													size="2xs"
													borderWidth="sm"
													showInnerContent={false}
												/>
												<QualityScorePercentage
													size="2xs"
													score={isPolicyDisabled ? undefined : scorePercentage}
													displayBadge={false}
												/>
											</Group>
										</Box>
										{index < entityViolations.policies.length - 1 && (
											<Divider orientation="vertical" />
										)}
									</>
								);
							})}
					</Group>
				</Card>
			</Group>
			<Title size="sm">Policies</Title>

			{isLoadingViolations && (
				<Center p="2xl">
					<Stack align="center" p="xl">
						<Loader />
					</Stack>
				</Center>
			)}

			{hasNoViolations && (
				<Center p="2xl">
					<Stack align="center" p="xl">
						<Icon name="circleCheck" size="lg" color="icon/secondary/default" />
						<Title size="sm">No policy violations found</Title>
						<Text size="sm" color="text/secondary/default">
							This entity is compliant with all active policies.
						</Text>
					</Stack>
				</Center>
			)}

			{hasViolations && (
				<Stack spacing="sm">
					{entityViolations.policies.map((policyBreakdown) => {
						const isExpanded = collapsedPolicies[policyBreakdown.policy.id];
						const isPolicyDisabled = !policyBreakdown.policy.enabled;
						const failingViolations = policyBreakdown.violations.filter(
							(v) => !v.is_passing
						);
						const issueCount = failingViolations.length;

						return (
							<Card key={policyBreakdown.policy.id} withBorder p={0}>
								{/* Policy Header Card */}
								<Box
									p="md"
									sx={(theme) => ({
										cursor: 'pointer',
										width: '100%',
										backgroundColor: isExpanded
											? theme.other.getColor('surface/primary/hover')
											: theme.other.getColor('surface/primary/default'),
										'&:hover': {
											backgroundColor: theme.other.getColor(
												'surface/primary/hover'
											),
										},
									})}
									onClick={() =>
										togglePolicyCollapse(policyBreakdown.policy.id)
									}
								>
									<Flex justify="space-between" align="center">
										<Group spacing="xs" align="center">
											<Text size="sm" weight="semibold">
												{policyBreakdown.policy.name}
											</Text>
											<Icon
												name={isExpanded ? 'chevronDown' : 'chevronRight'}
											/>
										</Group>
										<Group spacing="md">
											{isPolicyDisabled && (
												<Badge variant="neutral" size="sm">
													Disabled
												</Badge>
											)}
											{issueCount > 0 ? (
												<Group spacing="3xs">
													<Icon
														name="alertTriangleFilled"
														fillColor="icon/warning/default"
													/>
													<Text size="sm">
														{issueCount} issue{issueCount !== 1 ? 's' : ''}
													</Text>
												</Group>
											) : (
												<Group spacing="3xs">
													<Icon
														name="circleCheckFilled"
														fillColor="icon/success/default"
													/>
													<Text size="sm">OK</Text>
												</Group>
											)}
											{policyBreakdown.policy.frameworks &&
												policyBreakdown.policy.frameworks.length > 0 && (
													<Group spacing="2xs">
														{policyBreakdown.policy.frameworks.map(
															(framework) => (
																<Badge
																	key={framework}
																	variant="neutral"
																	size="sm"
																>
																	{framework}
																</Badge>
															)
														)}
													</Group>
												)}
											<Box
												onClick={(e: React.MouseEvent<HTMLDivElement>) => {
													e.stopPropagation();
													handleClick(policyBreakdown.policy.id);
												}}
												style={{ cursor: 'pointer', display: 'flex' }}
											>
												<Icon
													name="chevronRight"
													color="icon/secondary/default"
												/>
											</Box>
										</Group>
									</Flex>
								</Box>

								<Collapse in={isExpanded}>
									<Divider />
									<PolicyBreakdownList
										violations={policyBreakdown.violations}
									/>
								</Collapse>
							</Card>
						);
					})}
				</Stack>
			)}
		</Stack>
	);
}
